
import React, { useState } from "react";
import { DocumentProps } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { DocumentActionsDropdown } from "./DocumentActionsDropdown";
import { DocumentApprovalDialogComponent } from "./DocumentApprovalDialogComponent";
import { DocumentRejectionDialog } from "../DocumentRejectionDialog";
import { DocumentDeleteDialog } from "../DocumentDeleteDialog";
import { DocumentViewButton } from "./DocumentViewButton";
import { useDocumentVerificationHandlers } from "./useDocumentVerificationHandlers";
import { useIsMobile } from "@/hooks/use-mobile";

interface DocumentActionsCellProps {
  document: DocumentProps;
  onDocumentUpdated?: () => void;
}

export function DocumentActionsCell({ document, onDocumentUpdated }: DocumentActionsCellProps) {
  const isMobile = useIsMobile();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setPrimaryDialogOpen, setSetPrimaryDialogOpen] = useState(false);

  const {
    handleReject,
    handleApprove,
    handleSetPrimary,
    rejectNotes,
    setRejectNotes,
    approvalNotes,
    setApprovalNotes,
    isProcessing
  } = useDocumentVerificationHandlers({ 
    document, 
    onDocumentUpdated 
  });

  const canSetPrimary = document.verification_status === 'verified' && !document.primary_document;

  const handleApprovalDialogOpen = () => {
    setDropdownOpen(false);
    setApprovalDialogOpen(true);
  };

  const handleRejectionDialogOpen = () => {
    setDropdownOpen(false);
    setRejectionDialogOpen(true);
  };

  const handleDeleteDialogOpen = () => {
    setDropdownOpen(false);
    setDeleteDialogOpen(true);
  };

  const handleSetPrimaryDialogOpen = () => {
    setDropdownOpen(false);
    setSetPrimaryDialogOpen(true);
  };

  const handleApprovalConfirm = () => {
    handleApprove();
    setApprovalDialogOpen(false);
  };

  const handleRejectionConfirm = () => {
    handleReject();
    setRejectionDialogOpen(false);
  };

  return (
    <>
      <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-2'}`}>
        <DocumentViewButton 
          document={document} 
          variant="outline" 
          size={isMobile ? "sm" : "sm"}
          showText={!isMobile}
        />

        <DocumentActionsDropdown
          document={document}
          canSetPrimary={canSetPrimary}
          dropdownOpen={dropdownOpen}
          onDropdownOpenChange={setDropdownOpen}
          onApprovalDialog={handleApprovalDialogOpen}
          onRejectDialog={handleRejectionDialogOpen}
          onDeleteDialog={handleDeleteDialogOpen}
          onSetPrimaryDialog={handleSetPrimaryDialogOpen}
        />
      </div>

      <DocumentApprovalDialogComponent
        document={document}
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        approvalNotes={approvalNotes}
        onApprovalNotesChange={setApprovalNotes}
        onApprove={handleApprovalConfirm}
      />

      <DocumentRejectionDialog
        open={rejectionDialogOpen}
        onOpenChange={setRejectionDialogOpen}
        rejectNotes={rejectNotes}
        onRejectNotesChange={setRejectNotes}
        onReject={handleRejectionConfirm}
      />

      <DocumentDeleteDialog
        document={document}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDocumentDeleted={onDocumentUpdated}
      />
    </>
  );
}
